import {
  BASEMAP_TILE_PIXELS,
  directionToFaceSt,
  type LeafTree,
  pixelDirection,
  placeKey,
  type RasterType,
  type TilePixels,
  type TilePlace,
  tilesAcross,
  toCube,
  type Vector3,
} from "@pax/basemap";
import type { Orientation } from "./rasters";

/** A pixel of a raster: the tile holding it, and its column and row in that tile. */
export interface TilePixel {
  place: TilePlace;
  x: number;
  y: number;
}

/**
 * A raster as the editor holds it while it streams in: its tree of leaves, and the tiles loaded so
 * far, each 512 pixels at its own level. Edits touch leaves only; the places above them are made
 * again from them when a stroke is saved.
 */
export class TiledRaster {
  readonly tiles = new Map<string, TilePixels>();

  constructor(
    readonly name: string,
    readonly type: RasterType,
    /** Read each time, since a document change plants new trees. */
    private readonly treeOf: () => LeafTree,
    readonly orientation: Orientation,
  ) {}

  get tree(): LeafTree {
    return this.treeOf();
  }

  get(place: TilePlace): TilePixels | undefined {
    return this.tiles.get(placeKey(place));
  }

  set(place: TilePlace, pixels: TilePixels) {
    this.tiles.set(placeKey(place), pixels);
  }

  drop(place: TilePlace) {
    this.tiles.delete(placeKey(place));
  }

  /** Forgets every tile, as when the raster's base changes under them. */
  clear() {
    this.tiles.clear();
  }

  /** The leaf pixel under a world direction. */
  pixelAt(world: Vector3): TilePixel {
    const { face, s, t } = directionToFaceSt(toCube(this.orientation, world));
    const place = this.tree.leafAt(face, s, t);
    const across = tilesAcross(place.level);
    const clamp = (value: number) =>
      Math.min(BASEMAP_TILE_PIXELS - 1, Math.max(0, Math.floor(value)));
    return {
      place,
      x: clamp((s * across - place.x) * BASEMAP_TILE_PIXELS),
      y: clamp(((1 - t) * across - place.y) * BASEMAP_TILE_PIXELS),
    };
  }

  /** A pixel's value: its id, its count, or its four channels; null where it isn't loaded. */
  valueAt({ place, x, y }: TilePixel): number | number[] | null {
    const tile = this.get(place);
    if (!tile) return null;
    const index = y * BASEMAP_TILE_PIXELS + x;
    if (this.type === "color") return Array.from(tile.subarray(index * 4, index * 4 + 4));
    return tile[index] ?? null;
  }

  /** The value under a world direction, from whatever leaf holds it. */
  valueUnder(world: Vector3): number | number[] | null {
    return this.valueAt(this.pixelAt(world));
  }

  /** A pixel's middle, as a direction in the world. */
  directionOf({ place, x, y }: TilePixel): Vector3 {
    const level = place.level;
    const cube = pixelDirection(
      place.face,
      level,
      place.x * BASEMAP_TILE_PIXELS + x,
      place.y * BASEMAP_TILE_PIXELS + y,
    );
    // The orientation is a rotation, so its transpose takes the cube's frame back to the world's.
    const [a, b, c] = this.orientation;
    return [
      (a?.[0] ?? 0) * cube[0] + (b?.[0] ?? 0) * cube[1] + (c?.[0] ?? 0) * cube[2],
      (a?.[1] ?? 0) * cube[0] + (b?.[1] ?? 0) * cube[1] + (c?.[1] ?? 0) * cube[2],
      (a?.[2] ?? 0) * cube[0] + (b?.[2] ?? 0) * cube[1] + (c?.[2] ?? 0) * cube[2],
    ];
  }

  /**
   * The pixel beside one, `dx` and `dy` along, in the same tile or its neighbor at the same level
   * when that's loaded; null past a face's side or where nothing's loaded.
   */
  beside({ place, x, y }: TilePixel, dx: number, dy: number): TilePixel | null {
    let nx = x + dx;
    let ny = y + dy;
    let tx = place.x;
    let ty = place.y;
    if (nx < 0) [nx, tx] = [nx + BASEMAP_TILE_PIXELS, tx - 1];
    if (nx >= BASEMAP_TILE_PIXELS) [nx, tx] = [nx - BASEMAP_TILE_PIXELS, tx + 1];
    if (ny < 0) [ny, ty] = [ny + BASEMAP_TILE_PIXELS, ty - 1];
    if (ny >= BASEMAP_TILE_PIXELS) [ny, ty] = [ny - BASEMAP_TILE_PIXELS, ty + 1];
    const across = tilesAcross(place.level);
    if (tx < 0 || ty < 0 || tx >= across || ty >= across) return null;
    const next = { ...place, x: tx, y: ty };
    return this.get(next) ? { place: next, x: nx, y: ny } : null;
  }

  /** The leaves a cap of `radians` around `world` reaches, each once. */
  leavesInCap(world: Vector3, radians: number): TilePlace[] {
    const center = toCube(this.orientation, world);
    const found = new Map<string, TilePlace>();
    // The cap's middle and 64 points round its edge, and 2 rings between, find every leaf it
    // touches while the brush is no wider than a leaf or so; a wider one also takes the leaves
    // under a grid across its box.
    const helper: Vector3 = Math.abs(center[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
    const u = normalize(cross(center, helper));
    const v = cross(center, u);
    for (const share of [0, 0.35, 0.7, 1]) {
      const reach = radians * share;
      const count = share === 0 ? 1 : 64;
      for (let step = 0; step < count; step++) {
        const angle = (step / count) * Math.PI * 2;
        const cos = Math.cos(reach);
        const sin = Math.sin(reach);
        const point: Vector3 = [
          center[0] * cos + (u[0] * Math.cos(angle) + v[0] * Math.sin(angle)) * sin,
          center[1] * cos + (u[1] * Math.cos(angle) + v[1] * Math.sin(angle)) * sin,
          center[2] * cos + (u[2] * Math.cos(angle) + v[2] * Math.sin(angle)) * sin,
        ];
        const { face, s, t } = directionToFaceSt(point);
        const leaf = this.tree.leafAt(face, s, t);
        found.set(placeKey(leaf), leaf);
      }
    }
    return [...found.values()];
  }

  /**
   * Visits every loaded leaf pixel whose middle is within `radians` of `world`, with how far in it
   * is: 0 at the middle, 1 at the edge. Each leaf is read at its own size, so where detail differs
   * the brush paints each place's own pixels.
   */
  forEachInCap(
    world: Vector3,
    radians: number,
    visit: (pixel: TilePixel, distance: number) => void,
  ) {
    const center = normalize(toCube(this.orientation, world));
    const cos = Math.cos(radians);
    for (const place of this.leavesInCap(world, radians)) {
      if (!this.get(place)) continue;
      const base = { x: place.x * BASEMAP_TILE_PIXELS, y: place.y * BASEMAP_TILE_PIXELS };
      // The box the cap could cover in this tile, from where its middle falls and how many of the
      // tile's pixels its radius spans, with a pixel to spare.
      const middle = projectInto(place, center);
      const pixelRadians = Math.PI / 2 / (BASEMAP_TILE_PIXELS * tilesAcross(place.level));
      const reach = Math.ceil((radians / pixelRadians) * 1.5) + 2;
      const x0 = Math.max(0, Math.floor(middle.x - reach));
      const x1 = Math.min(BASEMAP_TILE_PIXELS - 1, Math.ceil(middle.x + reach));
      const y0 = Math.max(0, Math.floor(middle.y - reach));
      const y1 = Math.min(BASEMAP_TILE_PIXELS - 1, Math.ceil(middle.y + reach));
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          const direction = normalize(
            pixelDirection(place.face, place.level, base.x + x, base.y + y),
          );
          const dot =
            direction[0] * center[0] + direction[1] * center[1] + direction[2] * center[2];
          if (dot >= cos) visit({ place, x, y }, Math.acos(Math.min(1, dot)) / radians);
        }
      }
    }
  }
}

/** Where a cube-frame direction falls in a tile, in its pixels; outside it when it's elsewhere. */
function projectInto(place: TilePlace, direction: Vector3): { x: number; y: number } {
  const { face, s, t } = directionToFaceSt(direction);
  const across = tilesAcross(place.level);
  if (face !== place.face) {
    // On another face the middle is off this tile; the whole tile is searched.
    return { x: BASEMAP_TILE_PIXELS / 2, y: BASEMAP_TILE_PIXELS / 2 };
  }
  return {
    x: (s * across - place.x) * BASEMAP_TILE_PIXELS,
    y: ((1 - t) * across - place.y) * BASEMAP_TILE_PIXELS,
  };
}

const cross = (a: Vector3, b: Vector3): Vector3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const normalize = (v: Vector3): Vector3 => {
  const length = Math.hypot(v[0], v[1], v[2]) || 1;
  return [v[0] / length, v[1] / length, v[2] / length];
};
